import React, { useState, useEffect, useRef } from 'react';
import { Bell, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import './NotificationBell.css';

const NotificationBell = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const intervalRef = useRef(null);

    // Fetch notifications from backend (includes unreadCount)
    const fetchNotifications = async () => {
        try {
            const response = await api.get('/instances/notifications/recent');

            if (response.data.success) {
                const { notifications: newNotifications, unreadCount } = response.data.data;

                setNotifications(newNotifications);
                setUnreadCount(unreadCount || 0);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    // Mark notifications as seen on backend
    const markNotificationsAsSeen = async () => {
        try {
            await api.post('/instances/notifications/mark-seen');
            // Immediately update local state
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking notifications as seen:', error);
        }
    };

    // Initial fetch and setup polling - ONLY ONCE
    useEffect(() => {
        // Fetch immediately
        fetchNotifications();

        // Poll every 30 seconds
        intervalRef.current = setInterval(() => {
            fetchNotifications();
        }, 30000);

        // Cleanup on unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []); // EMPTY dependency array - run only once!

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleBellClick = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            // Mark as read when opening - save to database
            markNotificationsAsSeen();
        }
    };

    const handleNotificationClick = (notification) => {
        setIsOpen(false);
        navigate(notification.url);
    };

    const formatTimestamp = (timestamp) => {
        const now = new Date();
        const notifTime = new Date(timestamp);
        const diffMs = now - notifTime;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return 'Teraz';
        if (diffMins < 60) return `${diffMins} min temu`;

        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h temu`;

        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays}d temu`;
    };

    return (
        <div className="notification-bell-container" ref={dropdownRef}>
            <button
                className="notification-bell-button"
                onClick={handleBellClick}
                aria-label="Powiadomienia"
            >
                <Bell size={20} />
                {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
            </button>

            {isOpen && (
                <div className="notification-dropdown">
                    <div className="notification-header">
                        <h4>Ostatnie aktualizacje</h4>
                        <button
                            className="close-button"
                            onClick={() => setIsOpen(false)}
                            aria-label="Zamknij"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div className="notification-list">
                        {notifications.length === 0 ? (
                            <div className="notification-empty">
                                <Bell size={48} />
                                <p>Brak nowych aktualizacji</p>
                            </div>
                        ) : (
                            notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className="notification-item"
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="notification-content">
                                        <div className="notification-title">
                                            {notification.templateName}
                                        </div>
                                        <div className="notification-message">
                                            {notification.message}
                                        </div>
                                        <div className="notification-time">
                                            {formatTimestamp(notification.timestamp)}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {notifications.length > 0 && (
                        <div className="notification-footer">
                            <button
                                className="view-all-button"
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate('/instances');
                                }}
                            >
                                Zobacz wszystkie śledzenia
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
